import { Component, DestroyRef, effect, inject, input, signal, untracked, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from '@core/services/snackbar.service';
import { CONTACT_FORM } from '@modules/contacts/shared/consts/contact-form.const';
import { ContactTag } from '@modules/contacts/shared/interfaces/contact-tag.interface';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { NgSelectExtensionDirective } from '@shared/directives/ng-select-extension.directive';
import { ContactServiceV2 } from '@shared/modules/contact-sidenav/services/contact-v2.service';
import { catchError, EMPTY, filter, Subject, switchMap, tap } from 'rxjs';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { Config } from '@shared/configs/config';
import { ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-contact-tags',
  templateUrl: './contact-tags.component.html',
  styleUrls: ['./contact-tags.component.scss']
})
export class ContactTagsComponent {
  CONTACT_FORM = CONTACT_FORM;
  ButtonTypes = ButtonTypes;

  private readonly service = inject(ContactServiceV2);
  private readonly s = inject(SnackBarService);
  private readonly t = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private searchSubject = new Subject<string>();

  select = viewChild<NgSelectExtensionDirective | NgSelectComponent>('select');

  tagsTotalCount: number;
  tagsPage = 1;
  tags: ContactTag[] = [];
  searchTerm = '';

  tagsLoading = signal<boolean>(false);

  form = input.required<FormGroup>();

  get selectedTags() {
    const selectedItems = this.select()?.selectedItems || [];
    return selectedItems.sort((a, b) => a.value['name']?.localeCompare(b.value['name']));
  }

  constructor() {
    effect(() => {
      const tagControlValue = this.form().get(CONTACT_FORM.tagIds).value;
      if (tagControlValue) untracked(() => this.getInitTags());
    });
  }

  getInitTags({ search, openSelect }: { search?: string; openSelect?: boolean } = {}): void {
    const selectedIds = this.form().get(CONTACT_FORM.tagIds).value;
    if (selectedIds.some((element) => element === undefined)) return;

    this.tagsLoading.set(true);
    this.service
      .getTags({
        page: this.tagsPage,
        selectedIds,
        search
      })
      .pipe(
        tap(({ records, total }) => {
          this.tags = records;
          this.tagsTotalCount = total;
          this.tagsLoading.set(false);
          if (openSelect) (this.select() as NgSelectComponent).open();
        })
      )
      .subscribe();
  }

  addTagFn = ({ name, value }: { name: string; value: number }): void => {
    if (value || !name.trim()) return;

    this.tagsLoading.set(true);

    (this.select() as NgSelectComponent).close();

    this.service.addTag(name).subscribe(
      (addedTag: ContactTag) => this.onSuccessAddTag(addedTag),
      () => this.onErrorAddTag()
    );
  };

  onSuccessAddTag(addedTag: ContactTag): void {
    const control = this.form().get(CONTACT_FORM.tagIds);
    control.setValue([...control.value, addedTag.id].filter((id) => id));
    this.getInitTags({ openSelect: true });
    this.tagsLoading.set(false);
    this.s.success(this.t.instant('Contacts.Fields.successTagAdded'));
  }

  onErrorAddTag(): void {
    const control = this.form().get(CONTACT_FORM.tagIds);
    this.tagsLoading.set(false);
    control.setValue(control.value.filter((id) => id));
    this.getInitTags({ openSelect: true });
    this.s.error(this.t.instant('Contacts.Fields.errorTagAdded'));
  }

  fetchMoreTags(): void {
    const nextPage = this.tagsPage + 1;

    if (this.tagsTotalCount <= this.tags.length) return;

    this.service
      .getTags({ page: nextPage, selectedIds: this.form().get(CONTACT_FORM.tagIds).value })
      .pipe(
        tap(({ records }) => {
          this.tags = [...this.tags, ...records];
          this.tagsPage = nextPage;
        })
      )
      .subscribe();
  }

  onSearch({ term }) {
    if (this.searchTerm === term) return;
    this.tagsPage = 1;
    this.searchSubject.next(term);
  }

  removeTagFromApi(tagId: number, event: Event): void {
    event.stopPropagation();

    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'RemoveData.title',
          body: 'RemoveData.description',
          submitButtonText: 'RemoveData.confirm',
          submitButtonType: ButtonTypes.NEW_RED,
          submitButtonIcon: './assets/img/ic-warning-v2.svg'
        }
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        tap(() => this.tagsLoading.set(true)),
        switchMap(() =>
          this.service.removeTag(tagId).pipe(
            catchError(() => {
              this.tagsLoading.set(false);
              return EMPTY;
            })
          )
        ),
        tap(() => {
          this.removeSelectedTag(tagId);
          this.tags = this.tags.filter((tag) => +tag.value !== tagId);
          this.tagsLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  removeSelectedTag(tagId: string | number) {
    const control = this.form().get(CONTACT_FORM.tagIds);
    control.setValue(control.value?.filter((id) => Number(id) !== Number(tagId)));
  }
}
